import { setDateToUTCNoon } from "./date";

export const fetchExportConfig = async (query, clientId) => {
  if (!clientId) {
    console.warn(
      "Something went wrong in the application. No client ID provided."
    );
    return null;
  }
  try {
    const { data } = await query({
      variables: {
        clientId: clientId,
      },
      skip: !clientId,
    });
    if (!data?.exportConfig || !data?.exportConfig?.enabled) {
      console.warn(
        "No export config found or export is disabled for client ID: ",
        clientId
      );
      return null;
    }
    return data?.exportConfig;
  } catch (error) {
    console.error(
      "Error fetching export config for client ID: ",
      clientId,
      error
    );
    return null;
  }
};

/**
 * Validates the given service request for ORU export.
 *
 * @param {Object} serviceRequest - The service request to validate.
 * @param {string} serviceRequest.mrn - The medical record number of the patient.
 * @param {string} serviceRequest.lastName - The last name of the patient.
 * @param {string} serviceRequest.firstName - The first name of the patient.
 * @param {string} serviceRequest.dob - The date of birth of the patient (format: YYYY-MM-DD).
 * @param {string} serviceRequest.gender - The gender of the patient.
 * @param {string} serviceRequest.name - The name of the service request.
 * @throws {Error} If the service request is missing required data.
 * @throws {Error} If the patient's date of birth is in an incorrect format.
 */
export const validateExportORU = (serviceRequest) => {
  if (
    !serviceRequest?.mrn ||
    !serviceRequest?.lastName ||
    !serviceRequest?.firstName ||
    !serviceRequest?.dob ||
    !serviceRequest?.gender ||
    !serviceRequest?.name ||
    !serviceRequest?.accountNumber
  ) {
    throw new Error(`Failed to export. Service request is missing data.`);
  }

  if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(serviceRequest?.dob)) {
    throw new Error(`Failed to export. Patient DOB is incorrect format.`);
  }
};

/**
 * Hydrates the ORU export resource with the given parameters.
 *
 * @param {Object} params - The parameters for the export resource.
 * @param {Object} params.selectedDocument - The selected document.
 * @param {Object} params.serviceRequest - The service request details.
 * @param {Object} params.currentPatientsPool - The current patients pool.
 * @param {boolean} [params.isDefaultServiceRequestType=true] - Indicates if it is the default service request type.
 * @param {string|null} [params.selectedNewDocType=null] - The selected new document type.
 * @returns {Object} The hydrated ORU export resource.
 */
export const hydratedOruExportResource = ({
  selectedDocument,
  serviceRequest,
  currentPatientsPool,
  isDefaultServiceRequestType = true,
  selectedNewDocType = null,
}) => {
  const documentName =
    selectedNewDocType ??
    selectedDocument?.tags.find((tag) =>
      tag.key.includes("CaptureDocumentTags:documentType")
    ).value;
  const documentDescription =
    selectedDocument?.tags.find((tag) =>
      tag?.key?.includes("CaptureDocumentTags:documentDescription")
    )?.value || "";
  const additionalDescription =
    selectedDocument?.tags.find((tag) =>
      tag?.key?.includes("CaptureDocumentTags:additionalDocumentDescription")
    )?.value || "";
  const documentServiceCodes =
    selectedDocument?.tags
      .filter((tag) => tag?.key?.includes("CaptureDocumentTags:serviceCodes"))
      ?.map((tag) => tag?.value) || [];
  const mainDescription =
    documentDescription || additionalDescription || "No description";
  const hasDescriptionConcat = documentDescription && additionalDescription;
  const descriptionConcat = hasDescriptionConcat ? " - " : "";
  const departments = serviceRequest?.departments?.map((dept) => dept?.name);
  const srCodes =
    serviceRequest?.tenantServices?.map((code) => code?.name) || [];

  const mergeServiceCodes = [...documentServiceCodes, ...srCodes];

  return {
    documentOverrides: {
      documentDescription: `${mainDescription}${descriptionConcat}${
        descriptionConcat ? additionalDescription : ""
      }`,
      documentName,
      id: selectedDocument?.uuid,
    },
    documents: [selectedDocument?.uuid],
    patient: {
      patientPoolId: currentPatientsPool?.id,
      mrn: serviceRequest?.mrn,
      lastName: serviceRequest?.lastName,
      firstName: serviceRequest?.firstName,
      middleName: serviceRequest?.middleName,
      suffix: serviceRequest?.suffix,
      dob: serviceRequest?.dob,
      gender: serviceRequest?.gender,
    },
    encounter: {
      accountNumber: serviceRequest?.accountNumber,
      name: serviceRequest?.name,
      departmentNames: departments,
    },
    serviceCodes: mergeServiceCodes,
    providerNPI: serviceRequest?.orderingProvider?.npi,
    ...(!isDefaultServiceRequestType && {
      additionalProviderNPIs: serviceRequest?.additionalProviders?.[0]?.npi
        ? [serviceRequest?.additionalProviders?.[0]?.npi]
        : [],
    }),
    ...(!isDefaultServiceRequestType && {
      order: {
        id: serviceRequest?.orderId,
        resultDate: setDateToUTCNoon(serviceRequest?.resultDate),
        serviceDate: setDateToUTCNoon(serviceRequest?.serviceDate),
      },
    }),
  };
};
